import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";
import App_logo from "assets/images/BMAx_orderSystem.png";
import CircularProgress from "@mui/material/CircularProgress";
import { Box } from "@mui/material";
import { notifyCenter } from "utils/toast";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const post = async (body) => {
  // Plain fetch, not axiosInstance — this page runs logged-out and must not
  // attach a stale Bearer token (same pattern as VendorLink).
  const res = await fetch(`${API_BASE}/connect`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.json();
};

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // checking → form → invalid
  const [state, setState] = useState("checking");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await post({ action: "check_reset_token", token });
        if (!cancelled) setState(res?.status ? "form" : "invalid");
      } catch {
        if (!cancelled) setState("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (password.length < 8) {
      notifyCenter("error", "Passordet må være minst 8 tegn.", { duration: 3000 });
      return;
    }
    if (password !== confirm) {
      notifyCenter("error", "Passordene er ikke like.", { duration: 3000 });
      return;
    }
    setIsLoading(true);
    try {
      const res = await post({ action: "reset_password", token, password });
      if (!res?.status) {
        throw new Error(res?.message || "Noe gikk galt. Prøv på nytt.");
      }
      notifyCenter("success", "Passordet er oppdatert. Du kan nå logge inn.", { duration: 3000 });
      navigate("/authentication/log-in");
    } catch (err) {
      notifyCenter("error", err.message || "Noe gikk galt. Prøv på nytt.", { duration: 4000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CoverLayout
      header={
        <SoftBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            mb: 3,
          }}
        >
          <SoftBox component={Link} to="/" sx={{ maxWidth: 300, width: "100%" }}>
            <img src={App_logo} alt="App Logo" style={{ width: "100%", height: "auto", display: "block" }} />
          </SoftBox>
        </SoftBox>
      }
    >
      {(isLoading || state === "checking") && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <CircularProgress size={60} color="info" />
        </Box>
      )}
      {state === "invalid" ? (
        <SoftBox textAlign="center">
          <SoftTypography variant="h5" fontWeight="bold" mb={2}>
            Lenken er ugyldig eller utløpt
          </SoftTypography>
          <SoftTypography variant="button" color="text" fontWeight="regular">
            Be om en ny lenke for å tilbakestille passordet.
          </SoftTypography>
          <SoftBox mt={3}>
            <SoftButton
              component={Link}
              to="/authentication/forgot-password"
              variant="gradient"
              color="info"
              fullWidth
            >
              Be om ny lenke
            </SoftButton>
          </SoftBox>
        </SoftBox>
      ) : (
        <SoftBox component="form" role="form" onSubmit={handleSubmit}>
          <SoftBox mb={2} textAlign="center">
            <SoftTypography variant="h5" fontWeight="bold">
              Tilbakestill passord
            </SoftTypography>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              Velg et nytt passord (minst 8 tegn).
            </SoftTypography>
          </SoftBox>
          <SoftBox mb={3}>
            <SoftInput
              type="password"
              placeholder="Nytt passord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </SoftBox>
          <SoftBox mb={3}>
            <SoftInput
              type="password"
              placeholder="Gjenta nytt passord"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </SoftBox>
          <SoftBox mt={3} mb={1}>
            <SoftButton variant="gradient" color="info" fullWidth type="submit">
              Lagre nytt passord
            </SoftButton>
          </SoftBox>
          <SoftBox mt={2} textAlign="center">
            <SoftTypography
              component={Link}
              to="/authentication/log-in"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Tilbake til innlogging
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      )}
    </CoverLayout>
  );
}

export default ResetPassword;
